import React, { useMemo } from 'react';

import { Tag } from 'antd';

import styled from 'styled-components';

import { MODEL_AGENCY_TYPE_ORDERS } from '@constants/ModelAgencyTypes';

import { showDateNoTime } from '@libs/NwMoment';

import { useUserPermission } from '@contexts/UserContext';

import { useContactAgencies } from '@hooks/API/Contact/UseContactAgencies';

import CustomerDrawer from '@components/ContactDetails/CustomerDetails/Drawer/CustomerDrawer';
import EmptyState from '@components/Gui/EmptyState';
import NwDivider from '@components/Gui/NwDivider';
import NwIcon from '@components/Gui/NwIcon';
import StyleVariables from '@components/Gui/StyleVariables';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Wrapper = styled.div`
    padding: 0.5rem 0;
`;

const SectionTitle = styled.div`
    text-transform: uppercase;
    font-size: ${(props) => props.theme.FontSizeSm};
    font-weight: 600;
    /* color: ${(props) => props.theme.TextColorMuted}; */
    padding: 0.75rem 0 0.25rem;
    border-bottom: ${(props) => props.theme.DefaultBorder};
    margin-bottom: 0.5rem;
`;

const AgencyItem = styled.div`
    padding: 0.5rem 0;
    border-bottom: 1px solid ${(props) => props.theme.BorderColorLight || '#f0f0f0'};
    opacity: ${(props) => (props.$dropped ? 0.4 : 1)};

    .agency-row-main {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;

        .agency-icon-type {
            display: flex;
            flex-direction: column;
            align-items: center;
            min-width: 4rem;

            .agency-type-label {
                font-size: 0.6rem;
                text-transform: uppercase;
                color: ${(props) => props.theme.TextColorMuted};
                margin-top: 0.15rem;
            }
        }

        .agency-name-city {
            flex: 1;
            font-size: ${(props) => props.theme.FontSizeSm};

            .agency-name {
                font-weight: 600;
                text-transform: capitalize;
                ${(props) => props.$dropped && props.theme.RedLineThrough(1)};
            }

            .agency-city {
                color: ${(props) => props.theme.TextColorMuted};
                text-transform: capitalize;
            }
        }
    }

    .agency-row-details {
        padding: 0.25rem;
        font-size: ${(props) => props.theme.FontSizeSm};

        .agency-detail-line {
            margin-top: 0.25rem;
        }

        .agency-notes {
            margin-bottom: 0.25rem;
        }

        .agency-booker {
            font-weight: bold;
        }

        .agency-dates,
        .agency-contract {
            text-transform: uppercase;
            color: ${(props) => props.theme.TextColorMuted};
        }

        .agency-commission {
            font-weight: bold;
        }

        .agency-dropped-tag {
            margin-bottom: 0.25rem;
        }
    }
`;

const typeIconMap = {
    mother: 'medal',
    placement: 'handshake',
};

const AgencyIcon = ({ type }) => {
    const iconName = typeIconMap[type.toLowerCase()] || 'globe';
    return <NwIcon icon={byPrefixAndName.fasl[iconName]} />;
};

const ModelAgenciesCardMobile = ({ contactID }) => {
    const { data: currentContactAgencies } = useContactAgencies(contactID);
    const accessAccounting = useUserPermission('AccessAccounting');
    const [previewAgency, setPreviewAgency] = React.useState(null);

    const groupedAgencies = useMemo(() => {
        if (!currentContactAgencies) return {};

        const sorted = [...currentContactAgencies].sort((a, b) => {
            const dropDiff = a.Dropped - b.Dropped;
            if (dropDiff !== 0) return dropDiff;
            const orderDiff =
                (MODEL_AGENCY_TYPE_ORDERS[a.TypeName] ?? 99) - (MODEL_AGENCY_TYPE_ORDERS[b.TypeName] ?? 99);
            if (orderDiff !== 0) return orderDiff;
            return a.AgencyName > b.AgencyName ? 1 : -1;
        });

        const groups = {};
        sorted.forEach((agency) => {
            const type = agency.TypeName || 'Other';
            if (!groups[type]) groups[type] = [];
            groups[type].push(agency);
        });

        return groups;
    }, [currentContactAgencies]);

    const sectionOrder = ['Mother', 'Placement', 'Other'];
    const sections = sectionOrder.filter((type) => groupedAgencies[type]?.length > 0);

    if (!currentContactAgencies || currentContactAgencies.length === 0) {
        return <EmptyState message="no agencies" />;
    }

    return (
        <Wrapper>
            {sections.map((type) => (
                <div key={type}>
                    <SectionTitle>
                        {type}
                        {type !== 'Mother' ? 's' : ''}
                    </SectionTitle>
                    {groupedAgencies[type].map((agency) => (
                        <AgencyItem key={agency.ID} $dropped={agency.Dropped}>
                            <div className="agency-row-main">
                                <div className="agency-icon-type">
                                    <AgencyIcon type={agency.TypeName} />
                                    <span className="agency-type-label">{agency.TypeName}</span>
                                    {agency.Label && (
                                        <Tag
                                            color={`#${agency.Label.Color}`}
                                            style={{
                                                marginTop: '0.25rem',
                                                fontSize: '0.6rem',
                                                lineHeight: 1.5,
                                                padding: '0 0.25rem',
                                            }}
                                        >
                                            {agency.Label.Name}
                                        </Tag>
                                    )}
                                </div>
                                <div className="agency-name-city" onClick={() => setPreviewAgency(agency.AgencyID)}>
                                    <div className="agency-name">{agency.AgencyName.toLowerCase()}</div>
                                    {agency.Towns && (
                                        <div className="agency-city">
                                            {agency.Towns.split(',')
                                                .map((t) => t.trim().toLowerCase())
                                                .join(', ')}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <AgencyDetails agency={agency} accessAccounting={accessAccounting} />
                        </AgencyItem>
                    ))}
                </div>
            ))}
            {previewAgency && <CustomerDrawer customerID={previewAgency} onClose={() => setPreviewAgency(null)} />}
        </Wrapper>
    );
};

const AgencyDetails = ({ agency, accessAccounting }) => {
    const hasDetails =
        agency.Dropped ||
        agency.Notes ||
        (accessAccounting && agency.AccountingNotes) ||
        agency.Booker ||
        agency.ContractDate ||
        agency.DateIn ||
        agency.DateOut ||
        agency.Percentage > 0;

    if (!hasDetails) return null;

    return (
        <div className="agency-row-details">
            {agency.Dropped && (
                <div className="agency-dropped-tag">
                    <Tag color={StyleVariables.DangerColor}>DROPPED</Tag>
                </div>
            )}
            {agency.Notes && (
                <div className="agency-detail-line">
                    <NwDivider lessMargin>Booking Notes</NwDivider>
                    <div className="agency-notes">{agency.Notes}</div>
                </div>
            )}
            {accessAccounting && agency.AccountingNotes && (
                <div className="agency-detail-line">
                    <NwDivider lessMargin>Accounting Notes</NwDivider>
                    <div className="agency-notes">{agency.AccountingNotes}</div>
                </div>
            )}
            {agency.Booker && <div className="agency-detail-line agency-booker">Booker: {agency.Booker}</div>}
            {agency.ContractDate && (
                <div className="agency-detail-line agency-contract">
                    Contract signed on {showDateNoTime(agency.ContractDate)}
                </div>
            )}
            {!agency.DateIn && agency.DateOut && (
                <div className="agency-detail-line agency-dates">left on {showDateNoTime(agency.DateOut)}</div>
            )}
            {agency.DateIn && !agency.DateOut && (
                <div className="agency-detail-line agency-dates">since {showDateNoTime(agency.DateIn)}</div>
            )}
            {agency.DateIn && agency.DateOut && (
                <div className="agency-detail-line agency-dates">{`${showDateNoTime(agency.DateIn)} > ${showDateNoTime(agency.DateOut)}`}</div>
            )}
            {agency.Percentage > 0 && (
                <div className="agency-detail-line agency-commission">
                    {`${agency.Percentage}% comm. ${agency.OnGross ? ' on model gross' : ' on model net'}`}
                </div>
            )}
        </div>
    );
};

export default ModelAgenciesCardMobile;
